import { useEffect, useRef, useState } from "react";
import type { ICadModel } from "@faro-lotv/lotv";
import { AdaptivePointsMaterial } from "@faro-lotv/lotv";
import type { PointCloudObject } from "@/object-cache";
import { useSceneEvents } from "@/components/common/scene-events-context";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import {
  setClashRun,
  setClashStatus,
  consumeClashRequest,
  type CadPointCloudAnalysis,
} from "@/store/point-cloud-analysis-tool-slice";
import { selectAnalysis } from "@/store/point-cloud-analysis-tool-selector";
import {
  clashFingerprint,
  runCadClashAnalysis,
} from "./run-cad-clash-analysis";
import { ClashJobControl } from "./cad-clash-node-queue";
const PROGRESS_INTERVAL = 150;

/**
 *
 * @param cad Ready source model.
 * @param cloud Current source scan.
 * @param analysis Active saved comparison and review state.
 * @param budget CAD memory policy in bytes.
 */
export function useCadClashAnalysis(
  cad: ICadModel,
  cloud: PointCloudObject,
  analysis: CadPointCloudAnalysis,
  budget: number,
): void {
  const events = useSceneEvents();
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const [attempt, setAttempt] = useState(0);
  const consumedAttempt = useRef(0);
  const controller = useRef<AbortController>(undefined);
  const job = useRef<ClashJobControl>(undefined);
  const { id, cadReference, tolerance, clashClearance } = analysis;
  const requestedRun = useAppSelector(
    (state) => state.pointCloudAnalysisTool.execution?.[id]?.clashes ?? 0,
  );
  useEffect(() => {
    const subscription = events.cadClashCommand.on((request) => {
      if (request.analysisId !== id) return;
      if (request.command === "recompute") setAttempt((value) => value + 1);
      if (request.command === "pause") {
        job.current?.pause();
        dispatch(
          setClashStatus({
            analysisId: id,
            phase: "paused",
            message:
              "Pausing at the next work boundary; completed work is retained",
          }),
        );
      }
      if (request.command === "resume") {
        job.current?.resume();
        dispatch(
          setClashStatus({
            analysisId: id,
            phase: "loading",
            message: "Continuing queued work",
          }),
        );
      }
      if (request.command === "cancel") {
        controller.current?.abort();
        dispatch(
          setClashStatus({
            analysisId: id,
            phase: "cancelled",
            message: "Clash detection cancelled",
          }),
        );
      }
    });
    return () => subscription.dispose();
  }, [events, id, dispatch]);
  useEffect(() => {
    const active = new AbortController();
    const work = new ClashJobControl();
    job.current = work;
    controller.current = active;
    const current = selectAnalysis(id)(store.getState());
    const requested =
      attempt !== consumedAttempt.current ||
      requestedRun >
        (store.getState().pointCloudAnalysisTool.execution?.[id]
          ?.consumedClashes ?? 0);
    let lastUpdate = 0;
    async function run(): Promise<void> {
      if (current?.kind !== "cad") return;
      if (!requested && !current.clashes) {
        dispatch(setClashStatus({ analysisId: id, phase: "idle" }));
        return;
      }
      dispatch(
        setClashStatus({
          analysisId: id,
          phase: "loading",
          message: "Checking clash scope",
        }),
      );
      const hidden =
        cloud.material instanceof AdaptivePointsMaterial
          ? [...cloud.material.hiddenScans].sort((left, right) => left - right)
          : [];
      const fingerprint = await clashFingerprint(current, cloud, hidden);
      if (active.signal.aborted) return;
      if (current.clashes && !requested) {
        dispatch(
          setClashStatus({
            analysisId: id,
            phase:
              current.clashes.fingerprint === fingerprint ? "ready" : "stale",
            message:
              current.clashes.fingerprint === fingerprint
                ? undefined
                : "Settings or sources changed. Recompute clashes.",
          }),
        );
        return;
      }
      dispatch(
        setClashStatus({
          analysisId: id,
          phase: "loading",
          message: "Preparing clash detection",
        }),
      );
      consumedAttempt.current = attempt;
      dispatch(consumeClashRequest(id));
      const result = await runCadClashAnalysis(
        cad,
        cloud,
        current,
        budget,
        active.signal,
        (message) => {
          if (
            !active.signal.aborted &&
            performance.now() - lastUpdate > PROGRESS_INTERVAL
          ) {
            lastUpdate = performance.now();
            dispatch(
              setClashStatus({
                analysisId: id,
                phase: work.paused ? "paused" : "loading",
                message,
              }),
            );
          }
        },
        work,
      );
      active.signal.throwIfAborted();
      dispatch(setClashRun({ analysisId: id, run: result }));
      dispatch(setClashStatus({ analysisId: id, phase: "ready" }));
    }
    void run().catch((error: unknown) => {
      if (!active.signal.aborted) {
        dispatch(
          setClashStatus({
            analysisId: id,
            phase: "error",
            message:
              error instanceof Error ? error.message : "Clash detection failed",
          }),
        );
      }
    });
    return () => active.abort();
  }, [
    cad,
    cloud,
    id,
    cadReference,
    tolerance,
    clashClearance,
    budget,
    attempt,
    requestedRun,
    dispatch,
    store,
  ]);
}
